import {
	eachDayOfInterval,
	endOfWeek,
	format,
	isBefore,
	isSameDay,
	parseISO,
	startOfDay,
	startOfToday,
	startOfWeek,
	addDays,
	isWithinInterval,
	type Interval,
} from 'date-fns';
import type {ISbStoryData} from 'storyblok-js-client';
import {isEqualWith, uniq} from 'lodash-es';
import type {Day} from '~/types/openingHours';
import type {HolidayOpeningHoursStoryblok, PageTypeGasStationStoryblok} from '~/storyblok/types';
import {chunkBy, nonNullable} from '~/utils';

type HourRange = [string, string];

function capitalize(value: string): string {
	return value ? `${value.charAt(0).toLocaleUpperCase('cs')}${value.slice(1)}` : value;
}

interface ParsedDay {
	date: Date
	hours: HourRange[]
	isOpen: boolean
	isHoliday?: boolean
}

export interface OpeningHoursEntity {
	name: string
	hours: readonly Day[]
	holidays?: readonly HolidayOpeningHoursStoryblok[]
	open: boolean
	fullSlug?: string
}

function getWeek(date: Date): Interval {
	return {
		start: startOfWeek(date),
		end: endOfWeek(date),
	};
}

export function parseOpeningHours(pluginData: readonly Day[], holidayHours?: readonly HolidayOpeningHoursStoryblok[]) {
	const today = startOfToday();
	const thisWeek = getWeek(today);
	const thisYear = today.getFullYear();
	const startOfChristmasHolidays = new Date(thisYear, 11, 20);
	const endOfChristmasHolidays = new Date(thisYear + 1, 0, 5);
	const parsedHolidays: ParsedDay[] = holidayHours
		?.map((blok) => ({
			date: startOfDay(parseISO(blok.date)),
			hours: [[blok.openingHoursFrom!, blok.openingHoursTo!]] as HourRange[],
			isOpen: blok.state === 'open',
			isHoliday: true,
		}))
		.filter(({date}) => !isBefore(date, today)
			&& (isBefore(date, addDays(today, 14)) || isWithinInterval(date, {start: startOfChristmasHolidays, end: endOfChristmasHolidays})))
		.sort((a, b) => a.date.getTime() - b.date.getTime())
		?? [];

	const thisWeekDays = getParsedDays(thisWeek);
	const chunkedDays = chunkDays(thisWeekDays);
	const chunkedHolidays = chunkDays(parsedHolidays);

	return {
		today: thisWeekDays.find((d) => isSameDay(d.date, today))!,
		chunks: [...chunkedDays, ...chunkedHolidays],
		tableRowCount: chunkedDays.reduce((acc, chunk) => acc + chunk[0].hours.length, 0) + chunkedHolidays.length,
	};

	function getParsedDays(week: Interval): ParsedDay[] {
		const weekDays = eachDayOfInterval(week);

		return pluginData.map((day, index) => ({
			date: weekDays[index],
			hours: day.hours.length
				? day.hours.map(({start, end}) => [start, end])
				: [['00:00', '00:00']],
			isOpen: day.isOpen,
		}));
	}

	function chunkDays(days: ParsedDay[]): ParsedDay[][] {
		return chunkBy(days, (a, b) => (a.isOpen === b.isOpen && isEqualWith(a.hours, b.hours)) || (!a.isOpen && !b.isOpen))
			.map((chunk) => (
				// Split two-day holiday chunks into two one-day chunks to prevent unnecessarily long range labels
				chunk.length === 2 && chunk[0].isHoliday && chunk[0].isOpen && chunk[1].isOpen
					? [[chunk[0]], [chunk[1]]]
					: [chunk]
			))
			.flat(1);
	}
}

export function formatChunkDays(chunk: ParsedDay[], useFullDayName: boolean = false): string {
	// Show the exact date for holidays, otherwise show the day of the week
	const dayToken = useFullDayName ? 'eeee' : 'eeeeee';
	const formatString = chunk[0].isHoliday ? 'eeeeee d.M.' : dayToken;

	return uniq([chunk[0], chunk.at(-1)])
		.filter(nonNullable)
		.map((day: {date: number | Date;}) => capitalize(format(day.date, formatString)))
		.join(chunk.length > 2 ? ' - ' : ', ');
}

export function formatHourRange([start, end]: HourRange): string {
	return isHourRangeNonstop([start, end])
		? 'NONSTOP'
		: `${start} - ${end}`;
}

export function extractGasStationOpeningHours({content: story, full_slug: fullSlug}: ISbStoryData<PageTypeGasStationStoryblok>): OpeningHoursEntity[] {
	return [
		{id: 'fuelPump', name: 'Čerpací stanice', hash: 'informace'},
		{id: 'selfServiceFuelPump', name: 'Samoobslužné tankování'},
		{id: 'automaticCarWash', name: 'Mycí linka', hash: 'ceny-mycich-programu'},
		{id: 'manualCarWash', name: 'Samoobslužná mycí linka'},
	]
		.map(({id, name, hash}) => {
			const blok = story[id]?.[0];
			return blok
				? {
					component: blok.component,
					name,
					hours: blok.openingHours?.openingHours ?? [],
					holidays: blok.holidayOpeningHours,
					open: blok.open,
					fullSlug: dropUnderscoreRouteParts(fullSlug) + (hash ? `#${hash}` : ''),
				}
				: null;
		})
		.filter(nonNullable);
}

export function isHourRangeNonstop([start, end]: HourRange): boolean {
	return start === '00:00' && (end === '00:00' || end === '23:59');
}
